import { useState, useRef, useEffect } from 'react'
import { ErrorMessage, getIn, useFormikContext } from 'formik'
import classnames from 'classnames/bind'
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events'
import { useMeasure } from '@react-hookz/web'
import { CharIcon, FormError, ToolTip, LocationIcon } from '~/components'
import { searchLocation, searchNearby, fetchLocationInfo } from '~/lib/api'
import { formatAddressParts, formatLocationTitle } from '~/utils/format'
import { useDidMountEffect, usePrevious } from '~/utils/hooks'
import { default as SearchIcon } from './SearchIcon'
import * as styles from './index.module.css'
import { nanoid } from 'nanoid'
import { useTranslation } from 'react-i18next'

const cx = classnames.bind(styles)

const hasValue = field => field.value !== '' && field.value !== '-' && field.value?.address !== undefined

const formatLocationWithId = location => {
  // NOTE: Rename locationId to id
  const { locationId: id, ...rest } = location
  // Add unique id to every location object to use as React key in the search locations component.
  // This allows duplicate addresses, for example if departure and destination is the same but with multiple stops in between.
  if (rest?.address?.streetName && !rest.key) {
    rest.key = nanoid(10)
  }
  return { id, ...rest }
}

export const res = () => {console.log("res")}

export default function LocationSearch({
  fullscreen,
  field, // { name, value, onChange, onBlur }
  form: { errors, touched, setFieldValue, setFieldTouched }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  innerRef,
  useLocation = false,
  key,
  resetSearch,
  parentLocation,
  fieldIndex,
  ...props
}) {
  console.log("LocationSearch", props, fieldIndex)
  const { t } = useTranslation()
  const minChars = 3
  const [listSize, listRef] = useMeasure()
  const [listContentSize, listContentRef] = useMeasure()
  const { values } = useFormikContext()

  const [searchValue, setSearchValue] = useState(hasValue(field) ? formatLocationTitle(field.value) : '')
  const [locations, setLocations] = useState()

  const [currentIndex, setCurrentIndex] = useState(null)

  const [isVisible, setIsVisible] = useState(false)
  // const [hasGeolocation, setHasGeolocation] = useState(false)
  const [toolTipVisible, setToolTipVisible] = useState(false)
  const [redirectMessageVisible, setRedirectMessageVisible] = useState(false)
  const [redirectMessage, setRedirectMessage] = useState(false)
  const [currentExceptionZone, setCurrentExceptionZone] = useState(null)
  
  const [addressError, setAddressError] = useState(false)
  const isSelected = typeof field.value === 'object'

  const cityRef = useRef()
  const streetRef = useRef()

  const gotoNextLocationField = (currentFieldIndex) => {
    console.log("gotoNextLocationField", currentFieldIndex, fieldIndex);
    if(currentFieldIndex+1 === fieldIndex) {
      // Set a flag to indicate this is an automatic focus, not user-initiated
      // Use a longer timeout to ensure the flag persists through any Formik validation cycles
      window.AUTO_FOCUS_FIELD = field.name
      setTimeout(() => {
        innerRef.current?.focus()
        // Clear the flag after a longer delay to prevent validation during auto-focus
        setTimeout(() => {
          window.AUTO_FOCUS_FIELD = null
        }, 500)
      }, 100);
    }
    //
  }
  useCustomEventListener('GOTO_NEXT_LOCATION_FIELD', gotoNextLocationField)

  if (innerRef === undefined) innerRef = useRef()

  // Reset when clearing the search value
  const prevSearchValue = usePrevious(searchValue)

  // Reset component when the parent form is reset
  // NOTE: This only works when you have selected an address!
  const prevField = usePrevious(field)

  const reset = () => {
    setLocations()
    setCurrentIndex(null)
    setIsVisible(false)
    setCurrentExceptionZone(null)
    setAddressError(false)
    setRedirectMessageVisible(false)
  }

  useEffect(() => {
    if (field.name === 'departure' && field.value?.address?.connectedRegions?.length) {
      setFieldValue('region', field.value.address.connectedRegions.join(','))
    }
  }, [field.value])

  useDidMountEffect(() => {
    if (!hasValue(field)/* && hasValue(prevField)*/) {
      setSearchValue('')
      reset()
    }
    else if (hasValue(field)) {
      setSearchValue(formatLocationTitle(field.value))
      reset()
    }
  }, [field.value])

  useEffect(() => {
    //console.log("useEffect searchValue", searchValue, prevSearchValue);
    if (searchValue === '' && prevSearchValue?.length > 0) {
      // console.log('Reset location search component')
      reset()
    }
  }, [searchValue, prevSearchValue])

  // NOTE: Handle lost focus problem with the reorder component
  useEffect(() => {
    if (innerRef.current && window?.FIELD_FOCUS?.[field.name] === true) {
      innerRef.current.focus()
    }
  }, [innerRef])
  
  // Keep field untouched when auto-focused to prevent premature validation errors
  // Use a ref to track if this field was auto-focused, so we can prevent it from being touched
  const wasAutoFocusedRef = useRef(false)
  
  useEffect(() => {
    const isAutoFocused = window.AUTO_FOCUS_FIELD === field.name
    if (isAutoFocused) {
      wasAutoFocusedRef.current = true
      // Keep un-touching the field while it's auto-focused
      if (setFieldTouched && getIn(touched, field.name)) {
        setFieldTouched(field.name, false, false)
      }
    } else if (wasAutoFocusedRef.current && !getIn(touched, field.name)) {
      // Field was auto-focused but is no longer auto-focused and not touched
      // Reset the flag - user can now interact normally
      wasAutoFocusedRef.current = false
    } else if (wasAutoFocusedRef.current && getIn(touched, field.name)) {
      // Field has been explicitly touched without auto-focus active (e.g. submit validation)
      // Reset the flag so the error is allowed to render
      wasAutoFocusedRef.current = false
    }
  }, [touched, field.name, setFieldTouched])
 
  // Set scrollTop when switching lists
  useEffect(() => {
    if (isVisible && listRef.current) {
      listRef.current.scrollTop = fullscreen ? 0 : 50000
    }
  }, [locations, isVisible, listRef])

  // useEffect(() => {
  //   if (useLocation && navigator.geolocation) {
  //     setHasGeolocation(true)
  //   }
  // }, [])

  const search = value => {
    if (value.length >= minChars) {
      const r = field.name !== 'departure' ? values.region : null
      
      // For departure field in embed mode, prioritize provider location as reference
      let referencePosition = parentLocation?.address?.position
      const embedConfig = window.EMBED_CONFIG || {}
      if (field.name === 'departure' && embedConfig.isEmbed && embedConfig.providerLocation) {
        // Always use provider location for departure field in embed mode
        referencePosition = {
          latitude: embedConfig.providerLocation.latitude,
          longitude: embedConfig.providerLocation.longitude
        }
        console.log("[Embed] Using provider location as reference:", referencePosition)
      }
      
      console.log("search", referencePosition || parentLocation?.address?.position)
      searchLocation(value, r, cityRef.current, referencePosition)
        .then((locations) => {
          if (!fullscreen) locations.reverse()
          setIsVisible(true)
          setLocations(locations)
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error.message)
          }
        })
    }
    else if (value === '' && hasValue(field)) {
      setFieldValue(field.name, '-')
    }
    else if (isVisible) {
      setIsVisible(false)
      setCurrentExceptionZone(null)
    }
  }


  // -----------------------------------------------------
  //  Event handlers 
  // -----------------------------------------------------

  useCustomEventListener('CLEAR_LOCATION_SEARCH', (name) => {
    if(field.name === name) {
      console.log("RESET", name);
      setSearchValue('')
      reset();
    }
  });

  const handleChange = event => {
    const v = event.target.value
    if (streetRef.current && v.indexOf(streetRef.current) === -1) {
      cityRef.current = null
      streetRef.current = null
    }
    setSearchValue(v)
    setCurrentIndex(null)

    // Hide tooltip when changing input
    if (toolTipVisible) {
      setToolTipVisible(false)
    }

    // Hide error
    if (addressError !== false) {
      setAddressError(false)
    }

    search(v)
  }

  const handleKeyDown = event => {
    // Prevent arrow up/down to change cursor position in the input field
    if (event.keyCode == 38 || event.keyCode == 40) {
      event.preventDefault()
    }
  }

  const handleKeyUp = event => {
    if (locations?.length > 0) {
      if (event.keyCode == 13) {
        if (currentIndex != null) {
          handleSelect(currentIndex)
        }
        else if (locations.length === 1) {
          handleSelect(0)
        }
      }
      else if (event.keyCode == 38 || event.keyCode == 40) {
        if (currentIndex === null) {
          const index = fullscreen ? 0 : locations.length - 1
          setCurrentIndex(index)
        }
        else {
          const list = locations
          let ci = (event.keyCode == 38 ? currentIndex - 1 : currentIndex + 1)
          if (ci >= list.length) ci = list.length - 1
          else if (ci < 0) ci = 0
          setCurrentIndex(ci)

          if (listRef.current) {
            const listHeight = listSize.height
            const itemHeight = listContentSize.height / list.length

            const itemTop = ci * itemHeight
            const scrollTop = listRef.current.scrollTop
            if (itemTop + itemHeight > scrollTop + listHeight) {
              listRef.current.scrollTop = (itemTop + itemHeight) - listHeight
            }
            else if (itemTop < scrollTop) {
              listRef.current.scrollTop = itemTop
            }
          }
        }
      }
    }
  }

  const handleFocus = event => {
    // NOTE: Handle lost focus problem with the reorder component
    if (window.FIELD_FOCUS === undefined) window.FIELD_FOCUS = {}
    window.FIELD_FOCUS[field.name] = true
    setIsVisible(true)
    if(currentExceptionZone) emitCustomEvent('DRAW_EXEPTIONS_ZONE', currentExceptionZone);
    
    // If this is an automatic focus, ensure the field is NOT marked as touched
    // This prevents premature validation errors
    const isAutoFocus = window.AUTO_FOCUS_FIELD === field.name
    if (isAutoFocus && setFieldTouched) {
      // Explicitly un-touch the field if it was auto-focused
      setFieldTouched(field.name, false, false)
    }
    
    if (props.onFocus) props.onFocus(event)
  }

  const handleBlur = event => {
    // if (!useLocation) setIsVisible(false)
    // NOTE: Handle lost focus problem with the reorder component
    window.FIELD_FOCUS[field.name] = false
    // In mobile overlay mode keep the dropdown visible — the overlay handles closing
    if (props.mobileOverlay) return
    setIsVisible(false)
    
    // Only trigger Formik's onBlur if this wasn't an automatic focus transition
    // This prevents validation errors when focus moves automatically between fields
    const isAutoFocus = window.AUTO_FOCUS_FIELD === field.name
    if (!isAutoFocus) {
      field.onBlur(event)
    } else {
      // If this is an auto-focus field, prevent Formik from marking it as touched
      // by not calling field.onBlur, which would trigger validation
      // Also explicitly ensure it's not marked as touched
      if (setFieldTouched) {
        setFieldTouched(field.name, false, false)
      }
      console.log('[LocationSearch] Skipping onBlur for auto-focused field:', field.name)
    }
    if (props.onBlur) props.onBlur(event)
  }

  const getExeptionZoneInfo = (info) => {
    const title = info?.exceptionZone?.name ? info?.exceptionZone?.name  : '';
    const description = info?.locationInformation?.filter(item => ["EXCEPTION_ZONE_INFO"].includes(item?.tag))?.[0]?.message;
    return title + (description ? ' – ' + description : '');
  }

  const handleSelect = index => {
    console.log("handleSelect", index, locations);
    const item = locations[index]
    // If incomplete address (ie. a street name)
    if (item?.locationType === 'STREET') {
      setSearchValue(item.address.streetName + ' ')
      innerRef.current?.focus()
      if (field.name === 'departure') {
        setFieldValue('region', item.address.connectedRegions.join(','))
      }
      // Continue search only in this city
      cityRef.current = item.address.city
      streetRef.current = item.address.streetName
      setToolTipVisible(true)
      setCurrentIndex(null)
      search(item.address.streetName)
      return
    }
    setIsVisible(false)
    setCurrentExceptionZone(null);
    setSearchValue(formatLocationTitle(item))

    // NOTE: v3 if the item already has the locationInformation we do not need to fetch again
    if (item?.locationInformation) {
      setFieldValue(field.name, formatLocationWithId(item))
      emitCustomEvent('GOTO_NEXT_LOCATION_FIELD', fieldIndex);
      if (props.onMobileSelect) setTimeout(props.onMobileSelect, 100)
      return
    }
    
    fetchLocationInfo(item).then((info) => {
      console.log("exceptions");
      console.log(field, info)
      // NOTE: Handle redirectLocations here
      if (info?.bookable === false && info?.exceptionZone?.redirectLocations?.length > 0) {
        const redirectLocations = info?.exceptionZone?.redirectLocations?.map(formatLocationWithId)
        .filter(item => {
          // TODO: v3 Filter locations depending on TAG insted of type
          if (field.name === 'departure') return item?.locationInformation?.filter(info => ['NO_PICKUP'].includes(info?.tag)).length === 0
          if (field.name === 'destination') return item?.locationInformation?.filter(info => ['NO_DROP_OFF'].includes(info?.tag)).length === 0
          if (field.name.includes('intermediateStopLocations')) {
            return item?.locationInformation?.filter(info => ['NO_INTERMEDIATE_STOP', 'NO_DROP_OFF', 'NO_PICKUP'].includes(info?.tag)).length === 0
          } 
          return false
        })
        //value?.locationInformation?.filter(info => ['NO_INTERMEDIATE_STOP', 'NO_DROP_OFF', 'NO_PICKUP'].includes(info?.tag)).length === 0 || createError({ path, message: errorMessage })
        
        const locationMessage = getExeptionZoneInfo(info);
        if(info?.exceptionZone?.area?.coordinates?.[0]) {
          // If there is an exception zone, draw it on the map
          setRedirectMessageVisible(true)
          setRedirectMessage(locationMessage || t('info-select-redirect'))
          setCurrentExceptionZone({
            paths: info.exceptionZone.area.coordinates[0].map(coord => ({ lat: coord[1], lng: coord[0] })),
            locations: redirectLocations.map(item => ({lat: item.address.position.latitude, lng: item.address.position.longitude, locationId: item.id, mapExceptionZone: info?.exceptionZone})),
          })
          setIsVisible(true)
          console.log("DRAW EXEPTIONS ZONE");
          emitCustomEvent('DRAW_EXEPTIONS_ZONE', { 
            paths: info.exceptionZone.area.coordinates[0].map(coord => ({ lat: coord[1], lng: coord[0] })),
            locations: redirectLocations.map(item => ({lat: item.address.position.latitude, lng: item.address.position.longitude, locationId: item.id, mapExceptionZone: info?.exceptionZone})),
          });
        }
        if(redirectLocations.length === 0) {
          console.log("NO LOCATIONS");
          setFieldValue(field.name, formatLocationWithId(info))
          if (props.onMobileSelect) setTimeout(props.onMobileSelect, 100)
        } else {
          setRedirectMessageVisible(true)
          setRedirectMessage(locationMessage || t('info-select-redirect'))
          setLocations(redirectLocations.map(item => {
            return { mapExceptionZone: info?.exceptionZone, ...item }
          }))
          setIsVisible(true)
        }
        
        // TODO: v3 Filter locations depending on type=PICKUP/DROP_OFF/BOTH
        /*.filter(item => {
          if (field.name === 'departure' && item.type === 'DROP_OFF') return false
          if (field.name === 'destination' && item.type === 'PICKUP') return false
          return true
        })*/

        
        return
      } else if (info?.exceptionZone) {
        console.log("BOOKABLE EXCEPTION ZONE");
        const redirectLocations = info?.exceptionZone?.redirectLocations?.length > 0 ? [formatLocationWithId(info), ...info?.exceptionZone?.redirectLocations?.map(formatLocationWithId)].filter((item, id, arr) => {return arr.find((itemFind, index) => {console.log(index, id); return itemFind.id === item.id && index !== id}) === undefined}) : []
        const bookableLocation = formatLocationWithId(info);
        redirectLocations.push(bookableLocation);
        console.log("REDIRECT LOCATIONS", redirectLocations, bookableLocation);
        const locationMessage = getExeptionZoneInfo(info);
        if(locationMessage) {
          console.log("LOCATION MESSAGE", locationMessage);
          setRedirectMessage(locationMessage);
          setRedirectMessageVisible(true)
          setIsVisible(true)
          const bookableLocation = formatLocationWithId(info);
          bookableLocation.mapExceptionZone = info?.exceptionZone;
          setLocations(redirectLocations.map(item => {
            return { mapExceptionZone: info?.exceptionZone, ...item }
          }))
          //setFieldValue(field.name, bookableLocation);
        }
        if(info?.exceptionZone?.area?.coordinates?.[0]) {
          // If there is an exception zone, draw it on the map
          console.log("DRAW EXEPTIONS ZONE BOOKABLE");
          setCurrentExceptionZone({
            paths: info.exceptionZone.area.coordinates[0].map(coord => ({ lat: coord[1], lng: coord[0] })),
            locations: redirectLocations.map(item => ({lat: item.address.position.latitude, lng: item.address.position.longitude, locationId: item.id, mapExceptionZone: info?.exceptionZone})),
          })
          emitCustomEvent('DRAW_EXEPTIONS_ZONE', { 
            paths: info.exceptionZone.area.coordinates[0].map(coord => ({ lat: coord[1], lng: coord[0] })),
            locations: redirectLocations.map(item => ({lat: item.address.position.latitude, lng: item.address.position.longitude, locationId: item.id, mapExceptionZone: info?.exceptionZone})),
          });
        }
      } else {
        setFieldValue(field.name, formatLocationWithId(info))
        emitCustomEvent('GOTO_NEXT_LOCATION_FIELD', fieldIndex);
        if (props.onMobileSelect) setTimeout(props.onMobileSelect, 100)
      }
    })
    .catch((error) => {
      console.error(error.message)
      // NOTE: We cannot use setFieldError because that error will be overridden when the validation runs
      // so we use internal state and set a special "error" when an address could not be found.
      // if (error?.response?.error === 'LOCATION_NOT_FOUND') {
      //   setAddressError(error?.response?.message)
      // }
    })
  }

  // const fetchGeolocation = () => {
  //   navigator.geolocation.getCurrentPosition(
  //     (result) => {
  //       console.log(result)
  //       searchNearby(result.coords).then((locations) => {
  //         if (!isMobile) locations.reverse()
  //         console.log(locations)
  //         setCurrentIndex(null)
  //         setIsVisible(true)
  //         setLocations(locations)
  //       })
  //       .catch((error) => {
  //         console.error(error.message)
  //       })
  //     }, 
  //     (error) => {
  //       console.error(error)
  //     }
  //   )
  // }

  const handleIconClick = () => {
    if (field.value?.address) {
      emitCustomEvent('ZOOM_LOCATION', field.value)
    }
  }

  // -----------------------------------------------------
  //  Utils
  // -----------------------------------------------------

  const hasItems = () => {
    return (locations?.length > 0) || redirectMessageVisible
  }

  const formatItemForDropdown = item => {
    const o = formatAddressParts(item)
    return (
      <div className={styles.dropDown}>
        <LocationIcon location={item} className={styles.locationIcon} />
        <div className={styles.dropDownAddress}>
          {o.title}
          <span>{o.subtitle}</span>
        </div>
      </div>
    )
  }

  // -----------------------------------------------------
  //  Rendering 
  // -----------------------------------------------------

  const renderList = (fullscreen) => {
    // console.log("renderList");
    const list = locations
    
    if (list && list.length > 0) {
      return (
        <div className={cx(styles.dropdown, { fullscreen: fullscreen })}>
          {redirectMessageVisible && <div className={styles.message}>{redirectMessage}</div>}
          <div className={styles.scrollable} ref={listRef}>
            <ul ref={listContentRef}>
            {list.map((item, index) => (
              // NOTE: The onMouseDown prevents the onBlur event on the input field triggering and hiding the drop down before the onClick is called
              <li
                className={cx(styles.item, { selected: index === currentIndex })} 
                key={index} 
                onMouseDown={event => event.preventDefault()} onClick={() => handleSelect(index)}
                onMouseOver={() => emitCustomEvent('HOVER_LIST_ITEM', { 
                  item: item,
                })}
                onMouseOut={() => emitCustomEvent('CLEAR_ITEM_HOVER', { 
                  item: item,
                })}
              >{formatItemForDropdown(item)}</li>
            ))}
            </ul>
          </div>
        </div>
      )
    } else if(!list || list?.length === 0){
      // console.log("LIST EMPTY");
      return (
        <div className={cx(styles.dropdown, { fullscreen: fullscreen })}>
          <div className={styles.message}></div>
          {redirectMessageVisible && <div className={styles.message}>{redirectMessage}</div>}
        </div>
      )
    }
  }
  // NOTE: Override field error with the internal error stat if set
  const error = addressError || getIn(errors, field.name)
  // Don't show error if this field was auto-focused and hasn't been manually interacted with
  const isAutoFocused = window.AUTO_FOCUS_FIELD === field.name || wasAutoFocusedRef.current
  const touch = addressError !== false || (getIn(touched, field.name) && !isAutoFocused);
  const exceptionInfo = getExeptionZoneInfo(field?.value);
  const exceptionInfoVisible = field?.value?.exceptionZone?.redirectLocations?.length <= 0 &&exceptionInfo;

  return (
    <div className={styles.container}>
      <ToolTip title={t('info-enter-street-number')} isVisible={toolTipVisible} isMultiline />
      <div className={cx(styles.fieldwrapper, { dropDownVisible: isVisible && hasItems(), hasError: error && touch, upward: !fullscreen })}>
        <SearchIcon className={styles.icon} active={isVisible || searchValue !== ''} />
        {isSelected && <CharIcon char={'ABCDEFGHIJKLMNOPQRST'[fieldIndex]} className={styles.charIcon} onClick={handleIconClick} />}
        <input
          type="text"
          name={field.name}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          ref={innerRef}
          value={searchValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={props.placeholder}
          tabIndex={props.tabIndex !== undefined ? props.tabIndex : 0}
          autoFocus={!!props.mobileOverlay}
        />
        {/*hasGeolocation && <button type="button" tabIndex="-1" className={styles.locationbutton} onClick={fetchGeolocation}></button>*/}
        {renderList(fullscreen)}
      </div>
      {/* exceptionInfoVisible && <div style={{background:"#febf00", color:"black", fontSize:"12px", lineHeight:"15px", marginTop:"-4px", paddingTop:"8px", paddingBottom:"6px", paddingLeft:"8px", borderBottomLeftRadius:"4px", borderBottomRightRadius:"4px"}}>{exceptionInfo}</div> */}
      {error && touch && <FormError useMotion={false}>{error}</FormError>}
      {/* 
        
          NOTE: This is a workaround for the ErrorMessage component not working correctly with the Reorder.Group 
          causing the error message to display on the wrong location, duplicate messages and other bugs.

      */}
      {/* <ErrorMessage name={field.name} component={FormError} /> */}
    </div>
  )
}


